import { tv } from 'tailwind-variants';

import { CustomerType } from '@/entities/customer-type';
import { UserRole } from '@/entities/user-role';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { TaskCardType } from '@/task-manager/entities/task-card-type';
import { useFilterConfig } from '@/task-manager/hooks/use-filter-config';

import { ListTasks } from '../../api/queries/list-tasks';

import { AlertStatusFilter } from './alert-status-filter';
import { AlertTypeFilter } from './alert-type-filter';
import { AssigneeFilter } from './assignee-filter';
import { GroupFilter } from './group-filter';
import { TaskCardTypeFilter } from './task-card-type-filter';
import { TaskPriorityFilter } from './task-priority-filter';
import { TopFilter } from './top-filter';
import { VehicleFilter } from './vehicle-filter';
import { ViewModeFilter } from './view-mode-filter';
import { WorkstreamFilter } from './workstream-filter';

export type TasksFiltersProps = {
  filters: ListTasks.Filters;
  onChange: (filters: ListTasks.Filters) => void;
};

export function TasksFilters({ filters, onChange }: TasksFiltersProps) {
  const me = useMe();
  const { assetId } = useCurrentAsset();
  const featureFlag = useFeatureFlag();
  const { isProjectManagementMode, shouldHideCardType } =
    useFilterConfig(filters);
  const style = filterStyles();

  const showGroupFilter = me.config.type === CustomerType.BESS;
  const showVehicleFilter = me.config.type === CustomerType.MOBILITY;
  const showTopFilter = false; // me.config.type === CustomerType.HSS;

  function updateFilters(newFilters: ListTasks.Filters) {
    onChange({ ...filters, ...newFilters });
  }

  function updateCardTypeFilter(
    newCardType: TaskCardType | undefined,
    newFilters?: ListTasks.Filters,
  ) {
    let allFilters: ListTasks.Filters = {
      ...newFilters,
      cardType: newCardType,
    };
    Object.entries(defaultFilters).forEach(([cardType, filters]) => {
      if (newCardType !== cardType) {
        allFilters = { ...allFilters, ...filters };
      }
    });

    updateFilters(allFilters);
  }

  return (
    <div className={style.container()} data-testid="tasks-filters">
      <div className={style.filterList()}>
        <div className={style.filter()}>
          <TaskPriorityFilter
            value={filters.priority}
            onChange={(priority) => updateFilters({ priority })}
          />
        </div>
        {me.role !== UserRole.THIRD_PARTY ? (
          <div className={style.filter()}>
            <AssigneeFilter
              value={filters.assigneeEmail}
              onChange={(assigneeEmail) => updateFilters({ assigneeEmail })}
            />
          </div>
        ) : null}
        {me.role !== UserRole.THIRD_PARTY && showGroupFilter && !assetId ? (
          <div className={style.filter()}>
            <GroupFilter
              value={filters.groupId}
              onChange={(groupId) => updateFilters({ groupId })}
            />
          </div>
        ) : null}
        {me.role !== UserRole.THIRD_PARTY && showVehicleFilter && !assetId ? (
          <div className={style.filter()}>
            <VehicleFilter
              filters={filters}
              value={filters.topId}
              onChange={(topId) => updateFilters({ topId })}
            />
          </div>
        ) : null}
        {me.role !== UserRole.THIRD_PARTY && showTopFilter ? (
          <div className={style.filter()}>
            <TopFilter
              value={filters.topId}
              onChange={(topId) => updateFilters({ topId })}
            />
          </div>
        ) : null}
        {!isProjectManagementMode ? (
          <div className={style.filter({ className: 'max-w-[270px]' })}>
            <TaskCardTypeFilter
              value={filters.cardType}
              onChange={updateCardTypeFilter}
            />
          </div>
        ) : null}
        {featureFlag('alerts') && !shouldHideCardType(TaskCardType.ALERT) ? (
          <div className={style.filter()}>
            <AlertStatusFilter
              value={filters.alertStatusList}
              onChange={(alertStatusList) =>
                updateCardTypeFilter(TaskCardType.ALERT, { alertStatusList })
              }
            />
          </div>
        ) : null}
        {featureFlag('alerts') && !shouldHideCardType(TaskCardType.ALERT) ? (
          <div className={style.filter()}>
            <AlertTypeFilter
              value={filters.alertTypeList}
              onChange={(alertTypeList) =>
                updateCardTypeFilter(TaskCardType.ALERT, { alertTypeList })
              }
            />
          </div>
        ) : null}
        {featureFlag('projectManagement') && isProjectManagementMode ? (
          <div className={style.filter()}>
            <WorkstreamFilter
              filters={filters}
              assetId={assetId}
              value={filters.workstreamId}
              onChange={(workstreamId) =>
                updateCardTypeFilter(TaskCardType.STANDARD, { workstreamId })
              }
            />
          </div>
        ) : null}
      </div>
      {featureFlag('projectManagement') ? (
        <div className={style.viewModeToggle()}>
          <ViewModeFilter
            isProjectManagementMode={isProjectManagementMode}
            updateFilters={updateFilters}
          />
        </div>
      ) : null}
    </div>
  );
}

const defaultFilters: Record<TaskCardType, Partial<ListTasks.Filters>> = {
  [TaskCardType.STANDARD]: { workstreamId: undefined },
  [TaskCardType.ALERT]: {
    alertStatusList: undefined,
    alertTypeList: undefined,
  },
  [TaskCardType.DATA_ONBOARDING_INCIDENT]: {},
};

const filterStyles = tv({
  slots: {
    container: 'flex h-12 w-full items-start justify-between gap-4',
    filterList: 'flex gap-4',
    filter: 'min-w-32 max-w-52 flex-1',
    viewModeToggle: 'mb-2 flex-none self-end pb-1',
  },
});
